import { carouselNavigationItemSlotClassNames } from "@fluentui/react-northstar";
import { orderBy } from "lodash";
import defaultAxiosClient from "shared/service/api";
import ApiClient from "shared/service/api/ApiClient";
import {
    Client,
    DailySalesConfig_Public,
    DailySalesReport_Public,
    DailySalesSegmentIdentity_Public,
    DailySalesTenantConfig_Public,
    IDailySalesConfig_Public,
    IDailySalesSegmentIdentity_Public,
    IDailySalesTenantConfig_Public,
} from "shared/service/api/generated";
import { createSecureContext } from "tls";
import { SearchEntityTypeEnum, V360ReportData } from "view360Card/models";
import { V360ReportDataDto } from "view360Card/service/dtos/V360ReportDataDto";
import { MonthlyReportDataFromResponse } from "view360Card/store/MonthlyReportDataFromResponse";
import { Scope } from "../models";
import { DailyReportConfigSegment, DailySalesReport, DailySalesSegment } from "../models/daily-sales";
import { DailyReportConfig } from "../models/daily-sales/DailyReportConfig";
import { DailySalesSeries } from "../models/daily-sales/DailySalesSeries";

class ReportsService {
    private apiClient: Client;

    constructor(apiClient: Client) {
        this.apiClient = apiClient;
    }

    async getCustomersReport(
        intermediaryId: number,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        const path = `/secured/v360/sales-report/intermediary/${intermediaryId}/customer${this.getQueryParameters(
            scope,
            undefined,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        )}`;
        const response = await defaultAxiosClient.get<V360ReportDataDto>(path);
        return new MonthlyReportDataFromResponse(response.data).toDomainModel();
    }

    async getOtherParametersReport(
        intermediaryId: number,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        const path = `/secured/v360/sales-report/intermediary/${intermediaryId}/other${this.getQueryParameters(
            scope,
            undefined,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        )}`;
        const response = await defaultAxiosClient.get<V360ReportDataDto>(path);
        return new MonthlyReportDataFromResponse(response.data).toDomainModel();
    }

    async getSalesPersonSalesReport(
        salesPersonId: number,
        salesPersonIntermediaryNumber: string | undefined,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        return await this.getSalesReportBase(
            "sales-person",
            salesPersonId,
            salesPersonIntermediaryNumber,
            scope,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        );
    }

    async getIntermediarySalesReport(
        intermediaryId: number,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        return await this.getSalesReportBase(
            "intermediary",
            intermediaryId,
            undefined,
            scope,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        );
    }

    private async getSalesReportBase(
        prefix: string,
        id: number,
        salesPersonIntermediaryNumber: string | undefined,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        const path = `/secured/v360/sales-report/${prefix}/${id}/sales${this.getQueryParameters(
            scope,
            salesPersonIntermediaryNumber,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        )}`;
        const response = await defaultAxiosClient.get<V360ReportDataDto>(path);
        return new MonthlyReportDataFromResponse(response.data).toDomainModel();
    }

    async getSalesPersonActivityReport(
        salesPersonId: number,
        salesPersonIntermediaryNumber: string | undefined,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        return await this.getActivityReportBase(
            "sales-person",
            salesPersonId,
            salesPersonIntermediaryNumber,
            scope,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        );
    }

    async getIntermediaryActivityReport(
        intermediaryId: number,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        return await this.getActivityReportBase(
            "intermediary",
            intermediaryId,
            undefined,
            scope,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        );
    }

    private async getActivityReportBase(
        prefix: string,
        id: number,
        salesPersonIntermediaryNumber: string | undefined,
        scope: Scope | undefined,
        companyCode: string,
        segmentCodes: string[],
        yearCodes: string[],
        mode: string
    ): Promise<V360ReportData> {
        const path = `/secured/v360/sales-report/${prefix}/${id}/activity${this.getQueryParameters(
            scope,
            salesPersonIntermediaryNumber,
            companyCode,
            segmentCodes,
            yearCodes,
            mode
        )}`;
        const response = await defaultAxiosClient.get<V360ReportDataDto>(path);
        return new MonthlyReportDataFromResponse(response.data).toDomainModel();
    }

    public async getDailyReportConfig(): Promise<DailyReportConfig> {
        const response = await this.apiClient.user_dailysales_config_get();
        return {
            reloadIntervalSeconds: response.reloadIntervalSeconds ?? 0,
            companyList: (response.tenantList ?? []).map((t) => {
                return {
                    companyCode: t.tenantCode,
                    segmentList: (t.segmentList ?? []).map((s: DailyReportConfigSegment) => ({
                        code: s.code,
                        index: s.index,
                    })),
                };
            }),
        };
    }

    public async updateDailyReportConfig(dailyReportConfig: DailyReportConfig): Promise<void> {
        const configModel: IDailySalesConfig_Public = {
            reloadIntervalSeconds: dailyReportConfig.reloadIntervalSeconds,
            tenantList: dailyReportConfig.companyList.map((c) => {
                const tenantModel: IDailySalesTenantConfig_Public = {
                    tenantCode: c.companyCode,
                    segmentList: c.segmentList.map((s) => {
                        const segmentModel: IDailySalesSegmentIdentity_Public = {
                            code: s.code,
                            index: s.index,
                        };
                        return new DailySalesSegmentIdentity_Public(segmentModel);
                    }),
                };
                return new DailySalesTenantConfig_Public(tenantModel);
            }),
        };
        const model = new DailySalesConfig_Public(configModel);
        await this.apiClient.user_dailysales_config_put(model);
    }

    public async getDailySales(entityId: number, entityType: SearchEntityTypeEnum): Promise<DailySalesReport> {
        let response: DailySalesReport_Public;

        if (entityType === SearchEntityTypeEnum.Intermediary) {
            response = await this.apiClient.intermediary_dailysales_today(entityId);
        } else if (entityType === SearchEntityTypeEnum.SalesPerson) {
            response = await this.apiClient.salesperson_dailysales_today(entityId);
        } else {
            throw new Error("Unsupported entity type");
        }

        // MOCK section

        // response.tenantList?.forEach((t) => {
        //     t.segmentList?.forEach((s) => {
        //         s.premium = Math.floor(Math.random() * 10000);
        //         s.count = Math.floor(Math.random() * 100);
        //     });
        //     t.premium = t.segmentList
        //         ?.map((i) => i.premium)
        //         .reduce((a, current) => {
        //             return (a ?? 0) + (current ?? 0);
        //         });
        //     t.count = t.segmentList
        //         ?.map((i) => i.count)
        //         .reduce((a, current) => {
        //             return (a ?? 0) + (current ?? 0);
        //         });
        // });

        return {
            companyReports: orderBy(response.tenantList ?? [], (i) => i.orderIndex).map((company) => {
                return {
                    code: company.code,
                    colorScheme: company.colorScheme,
                    name: company.name,
                    orderIndex: company.orderIndex,
                    premium: company.premium,
                    count: company.count,
                    segmentList: orderBy(company.segmentList ?? [], (i) => i.segment?.orderIndex).map(
                        (segment): DailySalesSegment => ({
                            premium: segment.premium,
                            count: segment.count,
                            segmentLabel: {
                                code: segment.segment?.code,
                                description: segment.segment?.description,
                                orderIndex: segment.segment?.orderIndex,
                                selected: segment.segment?.selected,
                            },
                        })
                    ),
                    seriesList: (company.seriesList ?? []).map(
                        (series): DailySalesSeries => ({
                            code: series.code,
                            name: series.name,
                            tooltip: series.tooltip,
                        })
                    ),
                };
            }),
        };
    }

    private getQueryParameters(
        scope?: Scope,
        salesPersonIntermediaryNumber?: string,
        companyCode?: string,
        segmentCodes?: string[],
        yearCodes?: string[],
        mode?: string
    ) {
        const queryParams: string[] = [];

        if (scope) {
            queryParams.push(`reportScope=${scope.code}`);
        }

        if (salesPersonIntermediaryNumber) {
            queryParams.push(`intermediaryNumber=${salesPersonIntermediaryNumber}`);
        }

        if (companyCode) {
            queryParams.push(`companyCode=${companyCode}`);
        }

        if (segmentCodes && segmentCodes.length > 0) {
            queryParams.push(`segmentCodeList=${segmentCodes.join(",")}`);
        }

        if (yearCodes && yearCodes.length > 0) {
            queryParams.push(`yearCodeList=${yearCodes.join(",")}`);
        }

        if (mode) {
            queryParams.push(`reportMode=${mode}`);
        }

        return `?${queryParams.join("&")}`;
    }
}

export default new ReportsService(ApiClient);
